import { ApiResponse } from '@member-portal/types';

/**
 * Type for the authenticated request function that the app provides
 */
export type AuthenticatedRequestFn = (url: string, options?: RequestInit) => Promise<Response>;

/**
 * Configuration for creating an API client
 */
export interface ApiClientConfig {
  baseUrl?: string;
  getAuthenticatedRequest?: () => AuthenticatedRequestFn;
}

/**
 * Options for individual requests
 */
export interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * Base API client class that can be configured with authentication
 */
export class ApiClient {
  private baseUrl: string;
  private getAuthenticatedRequest?: () => AuthenticatedRequestFn;

  constructor(config: ApiClientConfig = {}) {
    this.baseUrl = config.baseUrl || '';
    this.getAuthenticatedRequest = config.getAuthenticatedRequest;
  }

  /**
   * Configure the client with an authenticated request function
   */
  setAuthenticatedRequestFn(fn: () => AuthenticatedRequestFn): void {
    this.getAuthenticatedRequest = fn;
  }

  /**
   * Make an authenticated request
   */
  private async makeRequest<T>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      let response: Response;

      if (options?.skipAuth || !this.getAuthenticatedRequest) {
        // Make unauthenticated request
        response = await fetch(url, options);
      } else {
        // Use the configured authenticated request function
        const makeAuthRequest = this.getAuthenticatedRequest();
        response = await makeAuthRequest(url, options);
      }

      if (!response.ok) {
        // Try to get the error message from the API response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // If we can't parse the error response, use generic messages
          if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to access this resource.';
          }
        }

        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      // Re-throw the error for the calling component to handle
      throw error;
    }
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string, options?: RequestOptions): Promise<T> {
    const response = await this.makeRequest<T>(endpoint, {
      method: 'GET',
      ...options,
    });

    if (!response.success) {
      throw new Error(response.message || 'Request failed');
    }

    return response.data;
  }

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    const headers = data instanceof FormData ? undefined : { 'Content-Type': 'application/json' };

    const response = await this.makeRequest<T>(endpoint, {
      method: 'POST',
      headers,
      body,
      ...options,
    });

    if (!response.success) {
      throw new Error(response.message || 'Request failed');
    }

    return response.data;
  }

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    const headers = data instanceof FormData ? undefined : { 'Content-Type': 'application/json' };

    const response = await this.makeRequest<T>(endpoint, {
      method: 'PUT',
      headers,
      body,
      ...options,
    });

    if (!response.success) {
      throw new Error(response.message || 'Request failed');
    }

    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, options?: RequestOptions): Promise<T> {
    const response = await this.makeRequest<T>(endpoint, {
      method: 'DELETE',
      ...options,
    });

    if (!response.success) {
      throw new Error(response.message || 'Request failed');
    }

    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T = any>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    const body = data instanceof FormData ? data : JSON.stringify(data);
    const headers = data instanceof FormData ? undefined : { 'Content-Type': 'application/json' };

    const response = await this.makeRequest<T>(endpoint, {
      method: 'PATCH',
      headers,
      body,
      ...options,
    });

    if (!response.success) {
      throw new Error(response.message || 'Request failed');
    }

    return response.data;
  }

  /**
   * Make a raw request and return the full ApiResponse
   */
  async request<T = any>(endpoint: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, options);
  }
}

/**
 * Create a new API client instance
 */
export function createApiClient(config?: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}
